import React, { useCallback, useContext, useEffect, useState } from 'react';
import { AngularContext } from 'react-app';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import t from 'react-translate';
import { getLecturePageByQuestionId, getPrimaryCourseCatalogId } from 'redux/selectors/course';
import { AnalyticsDashBoardType, AnalyticsPageType, AnalyticsTokenProps } from 'redux/schemas/app/analytics';

import { useQrveyInitializer } from 'analytics_dashboard/hooks/use-initialize-qrvey';
import useLoadToken from 'analytics_dashboard/hooks/use-load-token';
import useScript from 'shared/hooks/use-script';

import QrveyComponent from './qrvey-component';
import { config } from '@config/config.json';
import { AnalyticsContext } from './analytics-dashboard';
import CohortsSearchDropdown from './cohorts-search-dropdown';
import QrveyPanels from './qrvey-panels';
import { getBaseUrl } from './cohort-analytics';
import NpsCsatConfigurationWrapper from './nps-csat-stats/nps-csat-configuration-wrapper';

const QrveyPanelsWrapper = () => {
  const {
    dashboardType: type,
    pageIdKey,
    questionSetId,
    configKeyName,
    cohorts,
    analyticsPageType,
  } = useContext(AnalyticsContext);
  const { injectServices } = useContext(AngularContext);

  const institutionId = useSelector((state) => state.app.currentInstitutionId);
  const isLoading = useSelector(state => state.app.qrveyConfig.isLoading);
  const lecturePageId = useSelector(state => getLecturePageByQuestionId(state, {
    id: questionSetId,
    questionSetKey: analyticsPageType === AnalyticsPageType.COHORT_ANALYTICS ? 'cohort' : 'course',
  }));
  const primaryCourseCatalogId = useSelector(state => getPrimaryCourseCatalogId(state));

  const [cohortsFilter, setCohortsFilter] = useState<number[] | undefined>(undefined);
  const [cohortsCount, setCohortsCount] = useState(0);

  const [$state] = injectServices(['$state']);
  const { catalogId } = $state.params || {};

  const history = useHistory();

  const params: AnalyticsTokenProps = {
    type,
    catalogId,
    institutionId,
    pageIdKey,
    questionSetId,
    cohortCourseIds: cohortsFilter,
  };

  const showPanelsOnly = analyticsPageType === AnalyticsPageType.COHORT_ANALYTICS;

  const data = useLoadToken(params);

  // Load the Qrvey launcher script only when rendering the full dashboard (<qrvey-end-user> tag).
  // This script is not needed when showing individual panels only.
  const launcherStatus = useScript(showPanelsOnly ? null : config.qrvey.launcherUrl);

  // Load the Qrvey widget script only when rendering individual panels (<an-panel> tags).
  // This script enables panels to initialize and render independently without the full dashboard.
  const widgetStatus = useScript(showPanelsOnly ? config.qrvey.widgetUrl : null);

  // Determine readiness based on which mode is active:
  // - Full dashboard mode: requires launcherStatus to be ready
  // - Panels-only mode: requires widgetStatus to be ready
  const isReady = data && (showPanelsOnly ? widgetStatus === 'ready' : launcherStatus === 'ready');
  const BASE_URL = getBaseUrl(catalogId);

  const { panels, pageTitle, isError } = useQrveyInitializer(data, isReady, isLoading, configKeyName, pageIdKey, type);

  useEffect(() => {
    const cohortsParam = ($state.params?.cohorts?.split(',') ?? [])?.map(Number);
    if (cohortsParam) {
      setCohortsFilter(cohortsParam);
    }
  }, [$state.params?.cohorts]);

  useEffect(() => {
    const count = (cohortsFilter && cohortsFilter.length > 0)
      ? cohortsFilter.length
      : (cohorts?.length ?? 0);
    setCohortsCount(count);
  }, [cohortsFilter, cohorts]);

  const onUpdateFilter = (ids: number[]) => {
    setCohortsFilter(ids);
    history.push(`${BASE_URL}/quiz-and-survey/${questionSetId}?cohorts=${ids.join(',')}`);
  };

  const getPageTitle = useCallback(() => {
    if (!pageTitle) return null;
    const [mainTitle, subTitle] = pageTitle ?? ['', ''];

    return (
      <div className='d-flex flex-column align-items-center'>
        <span className='font-weight-bolder course-title-l mb-2'>
          {cohortsCount > 0
            ? t.COHORT_ANALYTICS.QUIZ_SURVEY_TITLE(cohortsCount, mainTitle)
            : mainTitle}
        </span>
        {lecturePageId ? (
          <a
            href={$state?.href('lecture-page', { catalogId: primaryCourseCatalogId ?? catalogId, id: lecturePageId })}
            className='font-weight-light text-primary course-title text-center question-title'
            target='_blank'
            rel='noreferrer'
          >
            {subTitle}
          </a>
        ) : (
          <span className='font-weight-light course-title text-center question-title'>{subTitle}</span>
        )}
      </div>
    );
  }, [$state, catalogId, cohortsCount, lecturePageId, pageTitle, primaryCourseCatalogId]);

  const renderHeader = (
    <div className='d-flex flex-column mt-6'>
      {getPageTitle()}
      {(type === AnalyticsDashBoardType.QUESTION_SET || type === AnalyticsDashBoardType.COHORT_QUESTION_SET) && (
        <NpsCsatConfigurationWrapper
          questionSetId={questionSetId}
          analyticsPageType={analyticsPageType}
        />
      )}
      {type === AnalyticsDashBoardType.COHORT_QUESTION_SET && (
        <CohortsSearchDropdown
          initialIds={cohortsFilter}
          onUpdateFilter={onUpdateFilter}
        />
      )}
    </div>
  );

  return (
    <QrveyComponent
      panels={panels?.length > 0 ? <QrveyPanels panels={panels} /> : null}
      renderHeader={renderHeader}
      isReady={isReady && !isError}
      showdashboard={!showPanelsOnly}
    />
  );
};

export default QrveyPanelsWrapper;
